import { PkSpinner, PkTab, PkTabs } from '@uefadigital/panenka-react';
import useThrottledCallback from 'beautiful-react-hooks/useThrottledCallback';
import useWindowScroll from 'beautiful-react-hooks/useWindowScroll';
import clsx from 'clsx';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useDrawStore } from '@apps/draw/store';
import { useLiveblog } from '@apps/draw-centre/hooks';
import { useDrawCentreOptions, useDrawCentreStore } from '@apps/draw-centre/store';
import { DrawTabId } from '@apps/draw-centre/store/types';
import { useWindowSize } from '@shared/hooks/useWindowSize';
import { useTranslateFn } from '@shared/Translate';
import { MatchStatus } from '@shared-types/match.types';
import GoogleTagManagerHelper from '@tracking/gtm-helper';

import * as styles from './styles.module.scss';

const DrawTabs = () => {
  const t = useTranslateFn();
  const { liveblog, isLoading: isLiveblogLoading } = useLiveblog();

  const {
    options: { isDrawArticleAvailable, drawId, pageName },
  } = useDrawCentreOptions();

  const tab = useDrawCentreStore((state) => state.tab);
  const setTab = useDrawCentreStore((state) => state.setTab);
  const videoHeight = useDrawCentreStore((state) => state.videoHeight);
  const draw = useDrawStore((state) => state.draw);

  const videoHeightRef = useRef<number>(videoHeight);
  const tabsWrapperRef = useRef(null);
  const onScroll = useWindowScroll();

  const { isMobile, isTablet } = useWindowSize();

  const [isFixed, setIsFixed] = useState(false);
  const [top, setTop] = useState<number>(0);

  const isSmallScreen = isMobile || isTablet;

  const initialLoadTracked = useRef(false);

  const handleScroll = useThrottledCallback(
    () => {
      if (!tabsWrapperRef.current) return;

      const { top: tabsTop } = tabsWrapperRef.current.getBoundingClientRect();
      const stickyHeight = Array.from(document.querySelectorAll('.navigation--sticky, .affix')).reduce(
        (acc, elem) => acc + elem.clientHeight,
        0,
      );

      const currentVideoHeight = videoHeightRef.current;
      const isFixed = tabsTop < stickyHeight + currentVideoHeight;

      // Manage height and state updates
      if (isFixed) {
        tabsWrapperRef.current.style.setProperty('height', `${tabsWrapperRef.current.clientHeight}px`);
      } else {
        tabsWrapperRef.current.style.removeProperty('height');
      }

      setIsFixed(isFixed);
      setTop(isFixed ? stickyHeight + currentVideoHeight : 0);
    },
    [isMobile, isTablet],
    100,
  );

  onScroll(handleScroll);

  useEffect(() => {
    const desktopLiveIndicator = document.querySelector('.draw-header--live-indicator');
    const mobileLiveIndicator = document.querySelector('.draw-header--live-indicator--mobile');
    const isLive = draw.status.eventStatus === MatchStatus.LIVE;

    if (desktopLiveIndicator) {
      desktopLiveIndicator.classList.toggle('hide', !isLive || isSmallScreen);
    }
    if (mobileLiveIndicator) {
      mobileLiveIndicator.classList.toggle('hide', !isLive || !isSmallScreen);
    }

    videoHeightRef.current = videoHeight;
  }, [draw.status.eventStatus, isSmallScreen, videoHeight]);

  const trackTab = useCallback(
    (pageName: string, pageSubType: string) => {
      GoogleTagManagerHelper.addPageMeta({
        pageName,
        pageSubType,
        draw_id: drawId,
        draw_name: draw?.translations?.title?.EN,
        draw_status: draw?.status.eventStatus.toUpperCase(),
      });
      GoogleTagManagerHelper.addVirtualPage({
        virtPath: pageName,
      });
    },
    [drawId, draw?.translations?.title?.EN, draw?.status.eventStatus],
  );

  const tabPriorities = useMemo(
    () => [
      {
        id: DrawTabId.RESULTS,
        available:
          draw?.status?.resultsAvailable &&
          draw.rounds?.some((r) =>
            r.result?.groups?.some((g) => g.teamSlots?.some((ts) => ts.teamIds?.length || ts.opponents?.length)),
          ),
        translationKey: 'draw_results_lbl',
        pageName: `/${pageName}/draws/${drawId}/`,
        pageSubType: 'detail',
      },
      {
        id: DrawTabId.UPDATES,
        available: draw?.status?.commentaryStatus !== MatchStatus.UPCOMING && liveblog?.id,
        translationKey: draw?.status?.commentaryStatus === MatchStatus.FINISHED ? 'draw_updates' : 'draw_live-updates',
        pageName: `/${pageName}/draws/${drawId}/updates/`,
        pageSubType: 'liveblog',
      },
      {
        id: DrawTabId.POTS,
        available: draw?.status?.potsAvailable && draw.rounds?.some((r) => r.pots?.every((p) => p.teamIds?.length)),
        translationKey: 'draw_pots',
        pageName: `/${pageName}/draws/${drawId}/pots/`,
        pageSubType: 'pots',
      },
      {
        id: DrawTabId.PROCEDURE,
        available: isDrawArticleAvailable,
        translationKey: 'draw_procedure',
        pageName: `/${pageName}/draws/${drawId}/procedure/`,
        pageSubType: 'article',
      },
    ],
    [
      draw.rounds,
      draw?.status?.commentaryStatus,
      draw?.status?.potsAvailable,
      draw?.status?.resultsAvailable,
      drawId,
      isDrawArticleAvailable,
      liveblog?.id,
      pageName,
    ],
  );

  const determineDefaultTab = useMemo(() => {
    return tabPriorities.find((tab) => tab.available)?.id || DrawTabId.PROCEDURE;
  }, [tabPriorities]);

  useEffect(() => {
    if (isLiveblogLoading) return;

    setTab(determineDefaultTab);
  }, [determineDefaultTab, setTab, isLiveblogLoading]);

  useEffect(() => {
    if (initialLoadTracked.current) return;

    const selectedTab = tabPriorities.find(({ id }) => id === determineDefaultTab);
    if (!selectedTab) return;

    trackTab(selectedTab.pageName, selectedTab.pageSubType);
    initialLoadTracked.current = true;
  }, [determineDefaultTab, tabPriorities, trackTab]);

  const handleChange = (event: CustomEvent<{ tabId: string }>) => {
    const selectedTab = tabPriorities.find((tab) => tab.id === event.detail.tabId);
    if (!selectedTab) return;
    setTab(selectedTab.id);
    trackTab(selectedTab.pageName, selectedTab.pageSubType);
    window.scrollTo({
      top: document.querySelector<HTMLDivElement>('.draw-right-column')?.offsetTop - videoHeightRef.current - 40,
      behavior: 'smooth',
    });
  };

  if (isLiveblogLoading)
    return (
      <div className={styles.loading}>
        <PkSpinner />
      </div>
    );

  return (
    <div ref={tabsWrapperRef} className={styles.tabs}>
      <div className={clsx(isFixed && styles.fixedTabs)} style={{ top: isFixed && `${top}px` }}>
        <PkTabs className={styles.drawTabs} alignment='left' onPkChange={handleChange}>
          {tabPriorities.map(({ id, available, translationKey }) => (
            <PkTab key={id} selected={tab === id} tabId={id} className={clsx(!available && styles.hiddenTab)}>
              {t(translationKey)}
            </PkTab>
          ))}
        </PkTabs>
      </div>
    </div>
  );
};

export default memo(DrawTabs);
